
import { useState } from 'react';

const hvacTypes = [
  {
    name: "Central Air Conditioning",
    desc: "High-efficiency central AC systems with SEER ratings up to 26, designed specifically for Florida's intense heat and humidity. Includes smart thermostat integration and zone control options.",
    image:
      "https://public.readdy.ai/ai/img_res/4b637befa0382fa0fbc391cf0e643e4e.jpg",
    specs: [
      "Up to 26 SEER Rating",
      "Variable Speed Compressor",
      "Smart Thermostat Ready",
      "Whisper-Quiet Operation",
    ],
  },
  {
    name: "Heat Pump Systems",
    desc: "Dual-function heat pumps that cool in summer and heat in winter, delivering up to 300% efficiency. Perfect for Florida's mild winters and scorching summers, reducing energy use by up to 50%.",
    image:
      "https://readdy.ai/api/search-image?query=Professional%20photograph%20of%20a%20modern%20white%20heat%20pump%20system%20installed%20outside%20a%20contemporary%20Florida%20home%2C%20sleek%20design%20with%20chrome%20accents%2C%20clean%20installation%20on%20raised%20platform%2C%20lush%20tropical%20landscaping%20in%20background%2C%20bright%20sunny%20day%2C%20professional%20HVAC%20product%20photography%20with%20sharp%20detail%20and%20warm%20natural%20lighting&width=600&height=400&seq=hvac-heat-pump-002&orientation=landscape",
    specs: [
      "300% Heating Efficiency",
      "Dual Cooling & Heating",
      "Inverter Technology",
      "10-Year Compressor Warranty",
    ],
  },
  {
    name: "Ductless Mini-Splits",
    desc: "Ideal for room additions, garages, or homes without ductwork. Each indoor unit is independently controlled for personalized comfort in every room, with ultra-quiet operation under 19 dB.",
    image:
      "https://readdy.ai/api/search-image?query=Professional%20interior%20photograph%20of%20a%20sleek%20modern%20white%20ductless%20mini-split%20air%20conditioning%20unit%20mounted%20on%20a%20clean%20white%20wall%20in%20a%20beautiful%20contemporary%20living%20room%2C%20elegant%20minimalist%20decor%2C%20warm%20natural%20lighting%20through%20large%20windows%2C%20tropical%20plants%20visible%2C%20sharp%20product%20photography%20showing%20the%20slim%20profile%20design&width=600&height=400&seq=hvac-mini-split-003&orientation=landscape",
    specs: [
      "Individual Zone Control",
      "Under 19 dB Noise Level",
      "No Ductwork Required",
      "Wi-Fi Enabled Controls",
    ],
  },
];

export default function HVACSection() {
  const [activeTab, setActiveTab] = useState(0);

  // Defensive guard: ensure activeTab is always a valid index
  const safeActiveTab = Math.min(Math.max(activeTab, 0), hvacTypes.length - 1);
  const activeItem = hvacTypes[safeActiveTab];

  return (
    <section id="hvac-systems" className="py-24 bg-brand-cream">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="flex items-center gap-3 justify-center mb-4">
            <div className="w-10 h-10 flex items-center justify-center bg-brand-red-light rounded-lg">
              <i className="ri-temp-cold-line text-brand-red text-xl"></i>
            </div>
            <span className="text-sm font-bold text-brand-red tracking-wider uppercase">
              HVAC Systems
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight max-w-3xl mx-auto">
            Stay Cool Year-Round With High-Efficiency HVAC
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Florida's heat demands more from your cooling system. We install top-rated HVAC
            systems engineered for maximum efficiency in hot, humid climates — keeping your
            home comfortable while cutting energy costs.
          </p>
        </div>

        {/* Tab navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {hvacTypes.map((type, i) => (
            <button
              key={type.name}
              onClick={() => setActiveTab(i)}
              className={`px-6 py-3 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTab === i
                  ? "bg-brand-red text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              {type.name}
            </button>
          ))}
        </div>

        {/* Active tab content */}
        <div className="flex flex-col lg:flex-row gap-12 items-center">
          <div className="flex-1 w-full">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={activeItem.image}
                alt={activeItem.name}
                className="w-full h-[450px] object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">{activeItem.name}</h3>
            <p className="text-gray-600 leading-relaxed mb-8 text-lg">{activeItem.desc}</p>

            <div className="space-y-4 mb-8">
              {activeItem.specs.map((spec) => (
                <div key={spec} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                  <div className="w-8 h-8 flex items-center justify-center bg-brand-red-light rounded-lg flex-shrink-0">
                    <i className="ri-check-line text-brand-red text-lg"></i>
                  </div>
                  <span className="font-medium text-gray-800">{spec}</span>
                </div>
              ))}
            </div>

            <a
              href="#consultation"
              className="inline-flex items-center gap-2 bg-brand-red text-white px-8 py-4 rounded-full font-semibold hover:bg-brand-red-dark transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Get HVAC Quote
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>

        {/* HVAC gallery strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
          {[
            {
              src: "https://readdy.ai/api/search-image?query=Professional%20photograph%20of%20an%20HVAC%20technician%20in%20clean%20uniform%20performing%20maintenance%20on%20a%20residential%20air%20conditioning%20unit%2C%20checking%20refrigerant%20levels%20with%20gauges%2C%20organized%20tools%20bright%20daylight%20Florida%20home%20exterior%20background%20commercial%20photography%20with%20warm%20tones&width=400&height=300&seq=hvac-maint-001&orientation=landscape",
              label: "Expert Maintenance",
            },
            {
              src: "https://readdy.ai/api/search-image?query=Professional%20photograph%20of%20a%20modern%20smart%20thermostat%20with%20digital%20display%20showing%20temperature%20and%20energy%20usage%20on%20a%20clean%20white%20wall%2C%20contemporary%20home%20interior%20warm%20ambient%20lighting%20sharp%20product%20photography%20showing%20the%20sleek%20round%20design%20with%20touch%20screen%20interface&width=400&height=300&seq=hvac-thermo-002&orientation=landscape",
              label: "Smart Controls",
            },
            {
              src: "https://readdy.ai/api/search-image?query=Professional%20photograph%20of%20clean%20white%20air%20ducts%20and%20vents%20in%20a%20modern%20residential%20home%20ceiling%2C%20bright%20LED%20lighting%20pristine%20installation%20showing%20professional%20ductwork%20contemporary%20interior%20design%20commercial%20photography%20with%20clean%20white%20tones%20and%20sharp%20detail&width=400&height=300&seq=hvac-ducts-003&orientation=landscape",
              label: "Ductwork Solutions",
            },
            {
              src: "https://readdy.ai/api/search-image?query=Professional%20photograph%20of%20a%20happy%20Florida%20family%20relaxing%20comfortably%20in%20their%20cool%20air-conditioned%20living%20room%2C%20modern%20contemporary%20interior%20with%20large%20windows%20showing%20tropical%20garden%20outside%20warm%20natural%20lighting%20genuine%20smiles%20lifestyle%20photography%20with%20warm%20inviting%20atmosphere&width=400&height=300&seq=hvac-comfort-004&orientation=landscape",
              label: "Home Comfort",
            },
          ].map((item) => (
            <div key={item.label} className="relative rounded-2xl overflow-hidden group cursor-pointer">
              <img
                src={item.src}
                alt={item.label}
                className="w-full h-[220px] object-cover object-top group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <p className="absolute bottom-4 left-4 text-white font-semibold text-sm">{item.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
